import React from 'react';
import { Pressable, View } from 'react-native';
import { periodLabel } from '@hrms/shared';
import { addMonths } from '../../../lib/date';
import { Card, EmptyState, Loading, Select, Text, radius, useTheme } from '../../../ui';

/** Month (YYYY-MM) select. */
export function PeriodSelect({ label = 'Period', value, onChange, past = 12, future = 0 }: { label?: string; value: string; onChange: (p: string) => void; past?: number; future?: number }) {
  const now = new Date();
  const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const opts = Array.from({ length: past + future + 1 }, (_, i) => addMonths(cur, future - i));
  if (!opts.includes(value)) opts.unshift(value);
  return <Select label={label} value={value} onChange={onChange} options={opts.map((p) => ({ value: p, label: periodLabel(p) }))} />;
}

export interface SideItem<K extends string | number> {
  key: K;
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}

/** Selectable list in a card (left rail on desktop; stacks above content on phones). */
export function SideMenu<K extends string | number>({ title, items, value, onChange, loading, emptyTitle = 'Nothing here', testID }: { title: string; items: SideItem<K>[]; value: K | null; onChange: (k: K) => void; loading?: boolean; emptyTitle?: string; testID?: string }) {
  const { colors } = useTheme();
  return (
    <Card title={title} flush testID={testID}>
      {loading ? (
        <Loading />
      ) : !items.length ? (
        <EmptyState title={emptyTitle} />
      ) : (
        <View style={{ padding: 6, gap: 2 }}>
          {items.map((it) => {
            const sel = it.key === value;
            return (
              <Pressable
                key={String(it.key)}
                accessibilityRole="button"
                accessibilityState={{ selected: sel }}
                onPress={() => onChange(it.key)}
                style={({ pressed }) => ({ flexDirection: 'row', alignItems: 'center', gap: 8, paddingHorizontal: 10, paddingVertical: 9, borderRadius: radius.md, backgroundColor: sel ? colors.primarySoft : pressed ? colors.hover : 'transparent' })}
              >
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text weight={sel ? 'semibold' : 'medium'} color={sel ? colors.primary : colors.text} numberOfLines={1}>
                    {it.title}
                  </Text>
                  {!!it.subtitle && (
                    <Text variant="small" numberOfLines={2}>
                      {it.subtitle}
                    </Text>
                  )}
                </View>
                {it.right}
              </Pressable>
            );
          })}
        </View>
      )}
    </Card>
  );
}

/** Formats a value from an arbitrary JSON-ish record for display. */
export const fmtVal = (v: unknown) => (v == null || v === '' ? '—' : typeof v === 'boolean' ? (v ? 'Yes' : 'No') : typeof v === 'object' ? JSON.stringify(v) : String(v));
